import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, MessageCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { TYPE_META } from "@/components/booking/TypePicker";
import { BookingAppointments } from "@/components/booking/BookingAppointments";
import { FacebookGuide } from "@/components/booking/FacebookGuide";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const Card = ({ title, children }) => (
  <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-4"><h3 className="font-display font-semibold">{title}</h3>{children}</section>
);
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;

export default function BookingSettings() {
  const { data } = useApi("/booking/settings");
  const invalidate = useRefresh();
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return <p className="text-slate-400">Caricamento…</p>;

  const link = `${window.location.origin}/prenota/${f.slug}`;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const toggle = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v].sort() });
  const save = async () => {
    try { await api.put("/booking/settings", { ...f, duration: Number(f.duration), min_notice_hours: Number(f.min_notice_hours), max_days: Number(f.max_days) }); invalidate(); toast.success("Prenotazioni aggiornate"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch { const t = document.createElement("textarea"); t.value = link; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove(); }
    toast.success("Link copiato");
  };
  const share = `https://wa.me/?text=${encodeURIComponent(`Ciao! Puoi fissare un appuntamento con me in autonomia qui: ${link}`)}`;

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="booking-settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Prenotazioni online</h1>
        <p className="text-slate-500 mt-1">I clienti fissano da soli un appuntamento dal calendario o chattando con l'assistente AI. Ogni prenotazione entra nei tuoi richiami.</p>
      </div>

      <section className="bg-[#0F172A] text-white rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-sky-300">Il tuo link di prenotazione</p>
            <p className="font-mono text-sm sm:text-base mt-1 break-all" data-testid="booking-link">{link}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label className="text-xs text-slate-300">Attiva</Label>
            <Switch data-testid="booking-enabled-switch" checked={f.enabled} onCheckedChange={set("enabled")} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="copy-booking-link" onClick={copy} variant="secondary" size="sm"><Copy className="h-4 w-4 mr-1.5" />Copia link</Button>
          <a data-testid="share-booking-wa" href={share} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-md bg-emerald-500 hover:bg-emerald-600 px-3 text-sm h-9"><MessageCircle className="h-4 w-4 mr-1.5" />Condividi su WhatsApp</a>
          <a data-testid="open-booking-page" href={link} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-md border border-white/20 hover:bg-white/10 px-3 text-sm h-9"><ExternalLink className="h-4 w-4 mr-1.5" />Apri pagina</a>
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Disponibilità">
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => (
              <button key={d} data-testid={`avail-day-${i}`} onClick={() => toggle("days", i)}
                className={`h-10 w-12 rounded-lg border text-sm font-medium transition-colors ${f.days.includes(i) ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-200 text-slate-500"}`}>{d}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Dalle"><Input data-testid="avail-start" type="time" value={f.start_time} onChange={set("start_time")} /></F>
            <F label="Alle"><Input data-testid="avail-end" type="time" value={f.end_time} onChange={set("end_time")} /></F>
            <F label="Pausa dalle"><Input data-testid="avail-lunch-start" type="time" value={f.lunch_start || ""} onChange={set("lunch_start")} /></F>
            <F label="Pausa alle"><Input data-testid="avail-lunch-end" type="time" value={f.lunch_end || ""} onChange={set("lunch_end")} /></F>
            <F label="Durata appuntamento (min)"><Input data-testid="avail-duration" type="number" min={10} step={5} value={f.duration} onChange={set("duration")} /></F>
            <F label="Preavviso minimo (ore)"><Input data-testid="avail-notice" type="number" min={0} value={f.min_notice_hours} onChange={set("min_notice_hours")} /></F>
            <F label="Prenotabile fino a (giorni)"><Input data-testid="avail-max-days" type="number" min={1} value={f.max_days} onChange={set("max_days")} /></F>
          </div>
          <p className="text-xs text-slate-500">Gli orari in cui hai già richiami o appuntamenti vengono esclusi automaticamente.</p>
        </Card>

        <Card title="Tipi di appuntamento e pagina">
          <div className="flex flex-wrap gap-2">
            {Object.entries(TYPE_META).map(([k, m]) => (
              <button key={k} data-testid={`avail-type-${k}`} onClick={() => toggle("types", k)}
                className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${f.types.includes(k) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200 text-slate-500"}`}>{m.label}</button>
            ))}
          </div>
          <F label="Indirizzo ufficio"><Input data-testid="booking-office" value={f.office_address || ""} onChange={set("office_address")} placeholder="Via Roma 1, Milano" /></F>
          <F label="Link videochiamata (Google Meet, Zoom…)"><Input data-testid="booking-video" value={f.video_link || ""} onChange={set("video_link")} placeholder="https://meet.google.com/..." /></F>
          <F label="Link personale"><Input data-testid="booking-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} /></F>
          <F label="Presentazione"><Textarea data-testid="booking-intro" rows={2} value={f.intro || ""} onChange={set("intro")} placeholder="Consulenza gratuita su mutui, prestiti e cessione del quinto." /></F>
          <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
            <div><p className="text-sm font-medium">Assistente AI in chat</p><p className="text-xs text-slate-500">Fa domande al cliente e propone gli orari liberi</p></div>
            <Switch data-testid="booking-ai-switch" checked={f.ai_enabled} onCheckedChange={set("ai_enabled")} />
          </div>
        </Card>
      </div>
      <Button data-testid="save-booking-settings" onClick={save} className="bg-[#0F172A] hover:bg-sky-600">Salva impostazioni</Button>
      <FacebookGuide link={link} onCopy={copy} />
      <BookingAppointments />
    </div>
  );
}
